import React from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LanguageIcon from "@mui/icons-material/Language";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";

import { useWidget } from "/imports/state/WidgetContext";
import { useImageFallback } from "/imports/ui/components/shared";

// wa.me expects country-code digits only — no "+", spaces or leading 00.
const waDigits = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  return d;
};

// Club-entered links often omit the protocol ("instagram.com/club").
const withProtocol = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const ICON_BUTTON_SX = {
  color: "primary.main",
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  transition: "all 0.15s ease",
  "&:hover": { bgcolor: "primary.main", color: "common.white", borderColor: "primary.main" }
};

// Icon + text inline link (desktop contact row).
function TextLink({ href, Icon, external, children }) {
  return (
    <Typography
      component="a"
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener" : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        minWidth: 0,
        fontSize: "0.82rem",
        fontWeight: 600,
        color: "text.secondary",
        textDecoration: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        "&:hover": { color: "primary.main" }
      }}
    >
      {/* The icon is the accent (secondary); the text stays grey so it reads. */}
      <Icon sx={{ fontSize: "1rem", flexShrink: 0, color: "secondary.main" }} />
      {children}
    </Typography>
  );
}

/**
 * Site-wide footer, kept to a slim bar: brand · inline contact links · social
 * icons, with a hairline caption row for © / Powered by. Contact details come
 * from the selected (or first) location; the WhatsApp number and online links
 * are the club-level fields. On mobile every link collapses into one icon row.
 * Renders nothing when the club has no contact info at all.
 */
function Footer() {
  const { widget, saas, locations, selectedLocation } = useWidget();
  const { pathname } = useLocation();
  const isCheckout = pathname.split("/").includes("checkout");

  const loc = selectedLocation || (locations || [])[0] || null;
  const name = saas?.name || widget?.name || "";
  const { showImage: showLogo, onImageError: onLogoError } = useImageFallback(saas?.logoUrl);
  const phone = loc?.phoneNumber || "";
  const email = loc?.email || "";
  const address = loc?.location || "";
  const whatsapp = waDigits(saas?.whatsappNumber || phone);
  const website = saas?.websiteLink || "";
  const instagram = saas?.instagramLink || "";
  const facebook = saas?.facebookLink || "";

  if (!phone && !email && !whatsapp && !website && !instagram && !facebook && !address) return null;

  const mapsHref = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : "";

  const socials = [
    whatsapp && { key: "whatsapp", href: `https://wa.me/${whatsapp}`, label: "WhatsApp", Icon: WhatsAppIcon },
    website && { key: "website", href: withProtocol(website), label: "Website", Icon: LanguageIcon },
    instagram && { key: "instagram", href: withProtocol(instagram), label: "Instagram", Icon: InstagramIcon },
    facebook && { key: "facebook", href: withProtocol(facebook), label: "Facebook", Icon: FacebookIcon }
  ].filter(Boolean);

  // Mobile shows every link as an icon, contact included.
  const mobileLinks = [
    phone && { key: "phone", href: `tel:${phone}`, label: `Call ${phone}`, Icon: PhoneOutlinedIcon },
    email && { key: "email", href: `mailto:${email}`, label: `Email ${email}`, Icon: EmailOutlinedIcon },
    address && { key: "map", href: mapsHref, label: "Directions", Icon: PlaceOutlinedIcon, external: true },
    ...socials.map((s) => ({ ...s, external: true }))
  ].filter(Boolean);

  const year = new Date().getFullYear();

  return (
    // xs bottom padding clears whichever fixed bar is up: the tall checkout bar
    // (terms + Total + Complete Booking) needs far more room than the ~56px
    // BottomNav on every other page — sizing both at the checkout height left a
    // long empty tail under the footer on mobile.
    <Box
      component="footer"
      sx={{ mt: { xs: 4, md: 6 }, borderTop: 1, borderColor: "divider", bgcolor: "background.paper", pb: { xs: isCheckout ? 14 : 8, md: 0 } }}
    >
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 2.5 }, pb: { xs: 1.5, md: 2 } }}>
        {/* Main row: brand · contact · socials (mobile: brand / icon row). */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: { xs: 1.25, md: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, minWidth: 0 }}>
            {showLogo && (
              <Box
                component="img"
                src={saas.logoUrl}
                alt={name}
                onError={onLogoError}
                sx={{ width: "1.75rem", height: "1.75rem", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
              />
            )}
            <Typography fontWeight={800} sx={{ fontSize: "0.95rem", letterSpacing: "-0.01em" }} noWrap>
              {name}
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              columnGap: 3,
              rowGap: 0.5,
              flex: 1,
              minWidth: 0
            }}
          >
            {phone && (
              <TextLink href={`tel:${phone}`} Icon={PhoneOutlinedIcon}>
                {phone}
              </TextLink>
            )}
            {email && (
              <TextLink href={`mailto:${email}`} Icon={EmailOutlinedIcon}>
                {email}
              </TextLink>
            )}
            {address && (
              <TextLink href={mapsHref} Icon={PlaceOutlinedIcon} external>
                {address}
              </TextLink>
            )}
          </Box>

          {socials.length > 0 && (
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.75, flexShrink: 0 }}>
              {socials.map(({ key, href, label, Icon }) => (
                <IconButton key={key} size="small" href={href} target="_blank" rel="noopener" aria-label={label} sx={ICON_BUTTON_SX}>
                  <Icon sx={{ fontSize: "1.125rem" }} />
                </IconButton>
              ))}
            </Box>
          )}

          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", flexWrap: "wrap", gap: 1 }}>
            {mobileLinks.map(({ key, href, label, Icon, external }) => (
              <IconButton
                key={key}
                size="small"
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener" : undefined}
                aria-label={label}
                sx={ICON_BUTTON_SX}
              >
                <Icon sx={{ fontSize: "1.125rem" }} />
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Caption row */}
        <Box
          sx={{
            mt: { xs: 1.25, md: 1.5 },
            pt: { xs: 1, md: 1.25 },
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", md: "space-between" },
            flexWrap: "wrap",
            columnGap: 1.5,
            rowGap: 0.25
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © {year} {name}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
            Powered by Apointa
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
